import { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Package, PencilLine } from 'lucide-react';
import { conversationsApi, unitTransport } from '../api/conversations';
import type { ConversationDetail } from '../api/conversations';
import { eventsApi } from '../api/events';
import { whatsNewApi } from '../api/whatsNew';
import type { EventDetail } from '../api/events';
import { loadEventTopics } from '../api/eventTopics';
import type { EventTopics } from '../api/eventTopics';
import { EventDocket } from './EventDocket';
import { EventRecord } from './EventRecord';
import { EventThread } from './EventThread';
import { AppBar, Footer, Gutter, Page } from '../ui/Chrome';
import type { Me } from '../api/auth';

/**
 * Event detail — the most important screen (spec §5A).
 *
 * The hierarchy is the 3px rule. Above it: paper, squared corners, a label/value table, a serif
 * title — a record. Below it: a sunken plane, rounded bubbles, sans throughout — talk.
 *
 * The thread cannot swamp the facts because it begins below a hard edge and sits on a darker
 * plane. The rule is never softened to a 1px divider, and the thread never shares the facts'
 * background (spec §10).
 *
 * **This screen used to be the whole page** — TROOP-116. Requirements, packing, transport and
 * photos each arrived as a full-height section stacked between the record and the thread, expanded
 * by default and fetching on mount, so an officer opening an ordinary campout met five management
 * surfaces and roughly seventeen requests before reaching the conversation. Worse, those sections
 * sat *below* the 3px rule on paper, which is precisely what broke §5A's claim that everything
 * below the rule is talk.
 *
 * They are now {@link EventDocket}'s rows, above the rule, built to the fact table's own geometry —
 * so the record and the things you can act on read as one continuous list of labels, and the rule
 * goes back to separating record from talk with nothing in between.
 *
 * What is left here is the shape of the page and nothing else. {@link EventRecord} and
 * {@link EventThread} are shared with the guardian's door, which had drifted into a different set
 * of fact labels and a button where the thread should be.
 */
export function EventDetailScreen({
  unitId,
  eventId,
  me,
  meId,
  canEdit,
  subgroupLabelSingular,
  subgroupLabelPlural,
  onBack,
  onEdit,
  onNavigate,
  onOpenThread,
}: {
  unitId: string;
  eventId: string;
  /** The signed-in person, for the account control the bar now carries. */
  me: Me;
  meId: string;
  /** Officer and above. The API is the enforcement point; this decides what renders. */
  canEdit: boolean;
  /** The unit's own word for a group — Patrol, Den, Crew. Rendered, never assumed (rule 16). */
  subgroupLabelSingular: string;
  subgroupLabelPlural: string;
  onBack: () => void;
  onEdit: () => void;
  /** For the account control the bar carries. Not the same as onBack or onOpenThread. */
  onNavigate: (path: string) => void;
  onOpenThread: (conversationId: string) => void;
}) {
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [thread, setThread] = useState<ConversationDetail | null>(null);
  const [topics, setTopics] = useState<EventTopics | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;

    eventsApi
      .detail(unitId, eventId)
      .then((loaded) => {
        if (!live) return;
        setEvent(loaded);

        // The event's own page is where it is shown in full, so opening it is seeing it -- the
        // second of the two places that mark an event seen, beside the agenda (TROOP-268). The
        // server records it only while the event is new, and a failure costs one more marker.
        whatsNewApi
          .markSeen(unitId, { announcements: [], events: [loaded.id] })
          .catch(() => undefined);

        if (loaded.conversationId) {
          conversationsApi
            .detail(unitId, loaded.conversationId)
            .then((d) => live && setThread(d))
            .catch(() => undefined);
        }
      })
      .catch(() => live && setLoadError('Could not load this event.'));

    // Its own request, beside the event's. The topics a reader may see is a policy answer per
    // topic (TROOP-179) and cannot ride on the event payload, which is a single row -- and it
    // must never be folded into the docket, whose sections are about what the event asks of a
    // family rather than about who may read what.
    loadEventTopics(unitId, eventId)
      .then((t) => live && setTopics(t))
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [unitId, eventId]);

  // Re-reads the event alone. The program is stored on the event rather than behind a route
  // of its own, so writing it has to reach this payload -- otherwise the panel saves and the
  // subtitle above it keeps last week's theme until a navigation. The thread is deliberately
  // not re-fetched: nothing about the program touches it.
  const reloadEvent = () => {
    eventsApi
      .detail(unitId, eventId)
      .then(setEvent)
      .catch(() => undefined);
  };

  // Re-reads the topics alone. Opening one changes nothing about the event or the general
  // thread, so reloading either would repaint the whole page for a row.
  const reloadTopics = () => {
    loadEventTopics(unitId, eventId)
      .then(setTopics)
      .catch(() => undefined);
  };

  const transport = useMemo(() => unitTransport(unitId), [unitId]);

  // One tree, whatever the state. Bailing out to a separate "loading" page used to unmount the
  // whole screen and mount a different one a moment later, so the chrome, the background planes
  // and the 3px rule all jumped -- and on the way back from the calendar the page visibly rebuilt
  // itself. Everything structural stays put; only the facts and the thread wait for their data.
  return (
    <Page>
      <AppBar
        parentLabel="Events"
        title={event?.title}
        onBack={onBack}
        me={me}
        currentUnitId={unitId}
        onNavigate={onNavigate}
      />

      {/* ---- Canonical block: the record, then the docket, then the hard edge ---- */}
      <section className="rule-canonical" style={{ background: 'var(--color-surface-page)' }}>
        <Gutter>
          {loadError && (
            <p
              className="t-body"
              role="alert"
              style={{ color: 'var(--color-text-secondary)', paddingBlock: 'var(--space-6)' }}
            >
              {loadError}
            </p>
          )}

          {event && (
            <>
              <EventRecord event={event} />

              <EventDocket
                unitId={unitId}
                event={event}
                meId={meId}
                subgroupLabelSingular={subgroupLabelSingular}
                subgroupLabelPlural={subgroupLabelPlural}
                // Only where a leader has one to open. Gated on the same canEdit the link below
                // uses, so the two doors into the summary agree about who is offered it.
                onOpenSummary={
                  canEdit
                    ? () => onNavigate(`/units/${unitId}/events/${eventId}/roster`)
                    : undefined
                }
                onEventChanged={reloadEvent}
              />

              {/* Below the spine, not inside it. A time that changed is corrected here, in the one
                  place everybody already reads, rather than announced in the thread and left to be
                  scrolled back for -- but it is a control rather than a fact, and putting it
                  between the fact rows and the docket rows cut the column of labels in half.

                  Absent entirely for a member: there is no disabled control to explain, because
                  the person who acts is the leader who owns the event, not the reader. */}
              {canEdit && (
                <p
                  className="flex flex-wrap items-center"
                  style={{ paddingBlock: 'var(--space-5)', gap: 'var(--space-4)' }}
                >
                  <button type="button" className="link-button t-body" onClick={onEdit}>
                    <span className="inline-flex items-center gap-2">
                      <PencilLine size={14} aria-hidden="true" />
                      Change these details
                    </span>
                  </button>

                  {/* TROOP-130, renamed by TROOP-137. Offered to officers, and the server
                      decides whether they may actually see it -- a youth officer passes this test
                      and is refused there, which is correct and is why the screen renders that
                      refusal as a sentence. Rendering it only for adults would need a second copy
                      of the two-axis rule here, and that copy is the one that drifts.

                      Still here as well as inside the Going and Forms panels: this is the way in
                      for somebody who wants the whole sheet, and those are the way in for somebody
                      already looking at one column of it. */}
                  <button
                    type="button"
                    className="link-button t-body"
                    onClick={() => onNavigate(`/units/${unitId}/events/${eventId}/roster`)}
                  >
                    <span className="inline-flex items-center gap-2">
                      <ClipboardCheck size={14} aria-hidden="true" />
                      Trip summary
                    </span>
                  </button>

                  {/* TROOP-167. Offered on the same terms as the summary above and judged by the
                      server on different ones: this is position only, so a youth Quartermaster --
                      who is refused the summary -- reaches the gear sheets. Rendering it only for
                      whoever passes both would need a copy of two rules here, and the copy is what
                      drifts. */}
                  <button
                    type="button"
                    className="link-button t-body"
                    onClick={() => onNavigate(`/units/${unitId}/events/${eventId}/gear`)}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Package size={14} aria-hidden="true" />
                      Gear sheets
                    </span>
                  </button>
                </p>
              )}
            </>
          )}
        </Gutter>
      </section>

      {/* ---- Thread: the talk. Sunken plane, below the hard edge. ----

          The band opens on a spine row rather than a heading of its own (TROOP-177) -- same 100px
          label column, same icon, same value column, same right-hand chevron as everything above
          the rule. The plane and the rule carry §5A's record/talk split; the vocabulary no longer
          changes across it, which is what made the one always-open section read as a different
          page bolted underneath. The row lives in {@link EventThread} so both doors get it. */}
      <section
        className="flex-1"
        style={{ background: 'var(--color-surface-sunken)', paddingBottom: 'var(--space-6)' }}
      >
        {/* Waits for the event too. The thread hangs off it, so rendering the header and an
            empty state first would announce "nothing asked yet" about an event nobody has
            loaded -- a statement that is briefly wrong rather than merely early. */}
        {event && (
          <EventThread
            transport={transport}
            conversationId={event.conversationId}
            detail={thread}
            meId={meId}
            topics={topics?.rows}
            unitId={unitId}
            eventId={eventId}
            // The server's answer for this caller, never `canEdit`. Opening a topic reads position
            // alone, so a youth Patrol Leader qualifies where the adult-officer checks elsewhere
            // on this page refuse them (TROOP-157's rule, applied again) -- and a flag frozen on
            // the client would fail silent, taking the control from the person who does have it.
            canCreateTopic={topics?.canCreate}
            // The unit's own word, threaded to the topic form the same way the docket already
            // takes it (rule 16). A Pack says Den here without a second screen existing.
            subgroupLabelSingular={subgroupLabelSingular}
            subgroupLabelPlural={subgroupLabelPlural}
            onTopicsChanged={reloadTopics}
            onOpenThread={onOpenThread}
          />
        )}
      </section>

      <Footer />
    </Page>
  );
}
